import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Button, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { CaseAdministrativeFeeModel } from 'app/edozvola/cases/cases.model';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCaseAdministrativeFee } from 'app/edozvola/cases/service/cases.service';
import { ERRORS, messages } from 'app/config/constants';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { fetchGeneralTariffCodebook } from 'app/edozvola/codebooks/codebooks.service';
import { CodebookModel } from 'app/shared/model/shared.model';
import { PaginatedDropdown } from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import { updateCaseAdministrativeFeeMutation } from "app/edozvola/cases/service/cases.queries";

const CaseAdministrativeFeeForm = () => {
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<CaseAdministrativeFeeModel>({} as CaseAdministrativeFeeModel);
  const [generalTariffCodebookOptions, setGeneralTariffCodebookOptions] = useState<CodebookModel[]>([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [readonly, setReadonly] = useState(true);
  const {mutate: saveMutation, isPending: isSaving} = updateCaseAdministrativeFeeMutation(id, (data) => {
    setInitialData(data);
    showToast(toast.success, messages.DATA_SAVED);
  }, (error) => {
    if (Object.values(ERRORS).includes(error.response?.data?.detail)) {
      showToast(toast.error, error.response?.data?.detail);
    } else {
      showToast(toast.error, ERRORS.GENERIC_ERROR);
    }
  });

  useEffect(() => {
    fetchCodebooks().then(() => {
      fetchCaseAdministrativeFee(id)
        .then(data => {
          setReadonly(data.readonly);
          setInitialData(data);
        })
        .catch(error => {
          if (Object.values(ERRORS).includes(error.response?.data?.detail)) {
            showToast(toast.error, error.response?.data?.detail);
          } else {
            showToast(toast.error, ERRORS.GENERIC_ERROR);
          }
          navigate('/cases/');
        });
    });
  }, [id]);

  const fetchCodebooks = async () => {
    await fetchGeneralTariffCodebook().then(data => setGeneralTariffCodebookOptions(data));
  };

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  const validation = Yup.object({
    generalTariff: Yup.object().required(messages.MANDATORY_FIELD),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    saveMutation(values);
  };

  return (
    <>
      <Formik enableReinitialize={true} validateOnMount={true} initialValues={initialData} validationSchema={validation} onSubmit={handleSubmit}>
        {({ errors, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <h5 className={'form-header'}>Opća upravna pristojba</h5>
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Opća pristojba'} />
                  <Field
                    id="generalTariff"
                    name="generalTariff"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={!readonly && 'Odaberi pristojbu'}
                        onItemSelect={selectedOption => setFieldValue('generalTariff', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={generalTariffCodebookOptions}
                        disabled={readonly}
                        error={!readonly && errors.generalTariff as string}
                        showErrorMessage
                        />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            {!readonly && (
              <div className="case-save-button">
                <Button
                  color={'info'}
                  label={'Spremi'}
                  isLoading={isSaving}
                  size={'lg'}
                  onClick={submitForm}
                  disabled={!dirty || !isValid}
                />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CaseAdministrativeFeeForm;
